/**
 * Proposals list page — /proposals
 *
 * Lists all active proposals filterable by status/category. Shows a "New
 * proposal" button for any logged-in member.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import {
  Loader2,
  Plus,
  FileText,
  ClipboardList,
  Vote,
  CheckCircle2,
  XCircle,
  CircleDashed,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft: { label: 'Draft', color: 'bg-slate-200 text-slate-700', Icon: FileText },
  discussion: { label: 'Discussion', color: 'bg-sky-100 text-sky-800', Icon: ClipboardList },
  voting: { label: 'Voting open', color: 'bg-amber-100 text-amber-800', Icon: Vote },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-600', Icon: CircleDashed },
};

const OUTCOME_META = {
  adopted: { label: 'Adopted', color: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', Icon: XCircle },
  no_quorum: { label: 'No quorum', color: 'bg-slate-100 text-slate-500', Icon: CircleDashed },
};

export default function ProposalsList() {
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (category !== 'all') params.category = category;
      const res = await axios.get(`${API}/proposals`, { params });
      setProposals(res.data.proposals || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, status, category]);

  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <h1 className="text-3xl font-bold mb-3">Sign in to view proposals</h1>
        <p className="text-muted-foreground mb-6">Governance is member-only.</p>
        <Button asChild><Link to="/auth/login">Sign in</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="proposals-page">
      <SEO title="Proposals — Club Governance" description="Vote on policies, plans, and projects." />
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Proposals</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Members shape the club: propose a policy, a plan, or a project — then vote it in.
          </p>
        </div>
        <Button asChild data-testid="proposals-new-btn">
          <Link to="/proposals/new"><Plus className="mr-2 h-4 w-4" />New proposal</Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-status-all">All</TabsTrigger>
            <TabsTrigger value="discussion" data-testid="filter-status-discussion">Discussion</TabsTrigger>
            <TabsTrigger value="voting" data-testid="filter-status-voting">Voting</TabsTrigger>
            <TabsTrigger value="closed" data-testid="filter-status-closed">Closed</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList>
            <TabsTrigger value="all">All types</TabsTrigger>
            <TabsTrigger value="policy">Policy</TabsTrigger>
            <TabsTrigger value="plan">Plan</TabsTrigger>
            <TabsTrigger value="project">Project</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : proposals.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No proposals here yet. Be the first — start a policy, plan, or project.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {proposals.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.draft;
            const outcome = p.outcome ? OUTCOME_META[p.outcome] : null;
            const StatusIcon = meta.Icon;
            return (
              <Link key={p.id} to={`/proposals/${p.id}`} data-testid={`proposal-row-${p.id}`}>
                <Card className="hover:border-amber-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge className={`${meta.color} border-0`}>
                            <StatusIcon className="mr-1 h-3 w-3" />
                            {meta.label}
                          </Badge>
                          <Badge variant="outline" className="capitalize">{p.category}</Badge>
                          {outcome && (
                            <Badge className={`${outcome.color} border-0`}>{outcome.label}</Badge>
                          )}
                        </div>
                        <p className="font-semibold text-slate-900">{p.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Proposed by {p.author_display_name || 'a member'} · {new Date(p.created_at).toLocaleDateString()}
                          {p.status === 'discussion' && (
                            <> · <strong>{p.seconds_count}/{p.seconds_needed}</strong> seconds</>
                          )}
                          {p.status === 'voting' && p.closes_at && (
                            <> · closes {new Date(p.closes_at).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
